import type { AxiosInstance } from 'axios';

export type ApiKeyStatus = 'ACTIVE' | 'REVOKED' | 'EXPIRED';
export type ApiKeyRateLimitTier = 'STANDARD' | 'PREMIUM' | 'INTERNAL';
export type SecurityEventKind =
  | 'LOGIN_SUCCESS'
  | 'LOGIN_FAILED'
  | 'LOGOUT'
  | 'PASSWORD_CHANGED'
  | 'TOKEN_REVOKED'
  | 'IP_NEW_LOCATION'
  | 'RATE_LIMIT_BREACH'
  | 'PERMISSION_DENIED'
  | 'WEBHOOK_SIGNATURE_INVALID'
  | 'API_KEY_USED'
  | 'API_KEY_REVOKED'
  | 'SUSPICIOUS_ACTIVITY';
export type SecurityEventSeverity = 'INFO' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type RolloutFlagStatus = 'DISABLED' | 'ROLLOUT' | 'ENABLED' | 'ARCHIVED';
export type UpstreamProvider =
  | 'RAZORPAY'
  | 'SHIPROCKET'
  | 'AWS_SES'
  | 'TWILIO'
  | 'META_WHATSAPP'
  | 'RAZORPAY_X'
  | 'CLOUDFLARE_R2'
  | 'AWS_S3';
export type UpstreamHealthGrade = 'GREEN' | 'YELLOW' | 'RED' | 'CIRCUIT_OPEN';

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  ownerUserId: string | null;
  status: ApiKeyStatus;
  rateLimitTier: ApiKeyRateLimitTier;
  expiresAt: string | null;
  lastUsedAt: string | null;
  notes: string | null;
  createdAt: string;
}

export interface IssuedApiKey extends ApiKey {
  plaintext: string;
}

export interface SecurityEvent {
  id: string;
  kind: SecurityEventKind;
  severity: SecurityEventSeverity;
  actorId: string | null;
  actorType: string;
  ip: string | null;
  userAgent: string | null;
  context: unknown | null;
  createdAt: string;
}

export interface RolloutFlag {
  id: string;
  key: string;
  description: string | null;
  status: RolloutFlagStatus;
  rolloutBps: number;
  enabledForUserIds: string[];
  enabledForRoles: string[];
  variants: unknown | null;
  createdAt: string;
}

export interface UpstreamHealthCheck {
  id: string;
  provider: UpstreamProvider;
  grade: UpstreamHealthGrade;
  latencyMs: number | null;
  failuresInWindow: number;
  totalInWindow: number;
  openedAt: string | null;
  recoveryAt: string | null;
  lastCheckedAt: string;
  lastError: string | null;
}

export function scaleEndpoints(http: AxiosInstance) {
  return {
    // API keys
    listApiKeys: () =>
      http.get<{ data: ApiKey[] }>('/admin/scale/api-keys').then((r) => r.data.data),
    issueApiKey: (body: {
      name: string;
      scopes: string[];
      ownerUserId?: string;
      rateLimitTier?: ApiKeyRateLimitTier;
      expiresAt?: string;
      notes?: string;
    }) => http.post<IssuedApiKey>('/admin/scale/api-keys', body).then((r) => r.data),
    revokeApiKey: (keyId: string, reason?: string) =>
      http.post(`/admin/scale/api-keys/${keyId}/revoke`, { reason }).then((r) => r.data),

    // Security events
    listSecurityEvents: (params?: {
      kind?: SecurityEventKind;
      severity?: SecurityEventSeverity;
      actorId?: string;
      limit?: number;
    }) =>
      http
        .get<{ data: SecurityEvent[] }>('/admin/scale/security/events', { params })
        .then((r) => r.data.data),

    // Rollout flags
    listFlags: () =>
      http.get<{ data: RolloutFlag[] }>('/admin/scale/flags').then((r) => r.data.data),
    upsertFlag: (body: {
      key: string;
      description?: string;
      status?: RolloutFlagStatus;
      rolloutBps?: number;
      enabledForRoles?: string[];
    }) => http.put<RolloutFlag>('/admin/scale/flags', body).then((r) => r.data),

    // Upstream health
    listUpstreams: () =>
      http.get<{ data: UpstreamHealthCheck[] }>('/admin/scale/upstreams').then((r) => r.data.data),
    recordProbe: (body: {
      provider: UpstreamProvider;
      success: boolean;
      latencyMs?: number;
      error?: string;
    }) => http.post('/admin/scale/upstreams/probe', body).then((r) => r.data),
  };
}

export type ScaleEndpoints = ReturnType<typeof scaleEndpoints>;
